import React from "react";

// Interface for individual learning objective
interface Objective {
  goal: string;
  description: string;
  icon: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  objectives?: Objective[];
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const LearningObjectivesSlide = {
  id: "learning-objectives-slide",
  name: "Learning Objectives Slide",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "Learning Objectives",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "What we'll learn today",
    },
    {
      id: "objectives",
      label: "Objectives",
      type: "array",
      maxItems: 8, // Changed from 9 to 8 to match the other slide
      defaultValue: [
        {
          goal: "Learning Goal 1",
          description: "Description of the first goal",
          icon: "🎯",
        },
      ],
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "linear-gradient(135deg, #0369a1 0%, #0284c7 100%)",
    },
  ],
  render: ({
    title = "Learning Objectives",
    subtitle = "What we'll learn today",
    objectives = [],
    bgColor,
  }: SlideContent) => {
    const limitedObjectives = objectives.slice(0, 8);

    // Calculate layout based on number of objectives
    const getGridLayout = (count: number) => {
      if (count <= 2) return "grid-cols-1";
      if (count <= 4) return "grid-cols-2";
      if (count <= 6) return "grid-cols-2";
      return "grid-cols-2";
    };

    // Text sizes based on count
    const getTextSizes = (count: number) => {
      if (count <= 2)
        return {
          goal: "text-4xl",
          description: "text-xl",
        };
      if (count <= 4)
        return {
          goal: "text-3xl",
          description: "text-lg",
        };
      if (count <= 6)
        return {
          goal: "text-2xl",
          description: "text-base",
        };
      return {
        goal: "text-xl",
        description: "text-sm",
      };
    };

    const gridClass = getGridLayout(limitedObjectives.length);
    const textSizes = getTextSizes(limitedObjectives.length);

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Hexagon Background Pattern */}
        <div className="absolute inset-0">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute opacity-20"
              style={{
                width: "120px",
                height: "104px",
                clipPath:
                  "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
                background: "rgba(255, 255, 255, 0.1)",
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                transform: "rotate(30deg)",
                animation: `float-hex-${i} ${15 + i * 2}s infinite linear`,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 flex h-full p-8">
          {/* Left Side - Title and Subtitle */}
          <div className="w-1/3 flex flex-col justify-center pr-8 border-r border-white/20">
            <div className="space-y-6">
              <h1 className="text-5xl font-black tracking-tight">{title}</h1>
              <div className="h-1 w-24 bg-white/30 rounded-full" />
              <p className="text-2xl font-light text-white/90">{subtitle}</p>

              {/* Decorative Elements */}
              <div className="flex space-x-2 mt-8">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="w-3 h-3 rounded-full bg-white/30"
                    style={{
                      animation: `pulse ${1 + i * 0.2}s infinite alternate ease-in-out`,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - Objectives */}
          <div className="w-2/3 pl-8">
            <div className={`grid ${gridClass} gap-6 h-full content-center`}>
              {limitedObjectives.map((objective, idx) => (
                <div
                  key={idx}
                  className="group relative bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 hover:bg-white/10 transition-all duration-300"
                >
                  {/* Background Accent */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-white/0 via-white/20 to-white/0" />

                  <div className="flex items-start gap-4">
                    {/* Left - Icon and Number */}
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center text-3xl transform group-hover:scale-110 transition-transform">
                        {objective.icon}
                      </div>
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </div>
                    </div>

                    {/* Right - Content */}
                    <div className="flex-grow">
                      <h3
                        className={`font-bold ${textSizes.goal} text-white/90 leading-tight mb-2`}
                      >
                        {objective.goal}
                      </h3>
                      <p
                        className={`${textSizes.description} text-white/70 leading-relaxed`}
                      >
                        {objective.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  },
};

// Updated styles
const style = document.createElement("style");
style.textContent = `
  ${[...Array(12)]
    .map(
      (_, i) => `
    @keyframes float-hex-${i} {
      0% { transform: translate(0, 0) rotate(${30 + i * 5}deg); }
      50% { transform: translate(${10 + i * 2}px, ${10 + i * 2}px) rotate(${60 + i * 5}deg); }
      100% { transform: translate(0, 0) rotate(${30 + i * 5}deg); }
    }
  `,
    )
    .join("\n")}
`;
document.head.appendChild(style);

export default LearningObjectivesSlide;
